import { Navigate, createBrowserRouter, type RouteObject } from 'react-router';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { NotFound, ProtectedShell, RootLayout } from '@/components/layout/route-shells';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { RouteError } from '@/components/layout/RouteError';

const page =
  <M,>(loader: () => Promise<M>, name: keyof M) =>
  async () => ({ Component: (await loader())[name] as React.ComponentType });

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      { path: '/entrar', element: <LoginPage /> },
      { path: '/cadastro', lazy: page(() => import('@/features/auth/pages/SignupPage'), 'SignupPage') },
      { path: '/acesso', lazy: page(() => import('@/features/auth/pages/MagicLinkPage'), 'MagicLinkPage') },
      { path: '/esqueci-senha', lazy: page(() => import('@/features/auth/pages/ForgotPasswordPage'), 'ForgotPasswordPage') },
      { path: '/redefinir-senha', lazy: page(() => import('@/features/auth/pages/ResetPasswordPage'), 'ResetPasswordPage') },
      { path: '/convite', lazy: page(() => import('@/features/auth/pages/AcceptInvitationPage'), 'AcceptInvitationPage') },
      { path: '/orientacao', lazy: page(() => import('@/features/advising/AcceptPages'), 'AcceptAdvisingPage') },
      { path: '/ingresso/:slug', lazy: page(() => import('@/features/advising/JoinPage'), 'JoinPage') },
      { path: '/convite-orientador', lazy: page(() => import('@/features/advising/AcceptPages'), 'AcceptAdvisorPage') },
      { path: '/privacidade', lazy: page(() => import('@/features/legal/PrivacyPage'), 'PrivacyPage') },
      {
        element: <RequireAuth />,
        children: [
          { path: 'trocar-senha', lazy: page(() => import('@/features/auth/pages/ChangePasswordRequiredPage'), 'ChangePasswordRequiredPage') },
          {
            element: <ProtectedShell />,
            children: [
              { index: true, lazy: page(() => import('@/features/dashboard/DashboardPage'), 'DashboardPage') },
              { path: 'trabalhos', lazy: page(() => import('@/features/works/pages/WorksListPage'), 'WorksListPage') },
              { path: 'trabalhos/novo', lazy: page(() => import('@/features/works/pages/NewWorkPage'), 'NewWorkPage') },
              { path: 'trabalhos/cadastrar', element: <Navigate to="/minhas-atividades" replace /> },
              { path: 'plano/:workId', lazy: page(() => import('@/features/plan/PlanPage'), 'PlanPage') },
              { path: 'minhas-atividades', lazy: page(() => import('@/features/plan/PlanPage'), 'MyPlanRedirect') },
              { path: 'orientandos', lazy: page(() => import('@/features/advising/AdviseesPage'), 'AdviseesPage') },
              { path: 'modelos', lazy: page(() => import('@/features/step-templates/TemplatesPage'), 'TemplatesPage') },
              {
                path: 'trabalhos/:id',
                lazy: page(() => import('@/features/works/WorkLayout'), 'WorkLayout'),
                children: [
                  { index: true, lazy: page(() => import('@/features/works/pages/WorkOverviewPage'), 'WorkOverviewPage') },
                  { path: 'etapas', lazy: page(() => import('@/features/steps/StepsPage'), 'StepsPage') },
                  { path: 'ficha', lazy: page(() => import('@/features/sheet/SheetPage'), 'SheetPage') },
                  { path: 'tarefas', lazy: page(() => import('@/features/tasks/TasksPage'), 'TasksPage') },
                  { path: 'entregas', lazy: page(() => import('@/features/deliverables/DeliverablesPage'), 'DeliverablesPage') },
                  { path: 'check-ins', lazy: page(() => import('@/features/checkins/CheckInsPage'), 'CheckInsPage') },
                  { path: 'reunioes', lazy: page(() => import('@/features/meetings/MeetingsPage'), 'MeetingsPage') },
                  { path: 'solicitacoes', lazy: page(() => import('@/features/requests/RequestsPage'), 'RequestsPage') },
                  { path: 'historico', lazy: page(() => import('@/features/history/HistoryPage'), 'HistoryPage') },
                  { path: 'ia', lazy: page(() => import('@/features/ai/AiPage'), 'AiPage') },
                  { path: 'cronograma', lazy: page(() => import('@/features/works/pages/WorkSchedulePage'), 'WorkSchedulePage') },
                  { path: 'participantes', lazy: page(() => import('@/features/works/pages/WorkParticipantsPage'), 'WorkParticipantsPage') },
                  { path: 'politica', lazy: page(() => import('@/features/works/pages/WorkPolicyPage'), 'WorkPolicyPage') },
                ],
              },
              { path: 'revisao', lazy: page(() => import('@/features/review/ReviewQueuePage'), 'ReviewQueuePage') },
              { path: 'agenda', lazy: page(() => import('@/features/agenda/AgendaPage'), 'AgendaPage') },
              { path: 'notificacoes', lazy: page(() => import('@/features/notifications/NotificationsPage'), 'NotificationsPage') },
              { path: 'relatorios', lazy: page(() => import('@/features/reports/ReportsPage'), 'ReportsPage') },
              { path: 'instituicoes', lazy: page(() => import('@/features/institutions/pages/InstitutionsListPage'), 'InstitutionsListPage') },
              { path: 'instituicoes/:id', lazy: page(() => import('@/features/institutions/pages/InstitutionAdminPage'), 'InstitutionAdminPage') },
              { path: 'politicas', lazy: page(() => import('@/features/policies/pages/PoliciesListPage'), 'PoliciesListPage') },
              { path: 'politicas/:id', lazy: page(() => import('@/features/policies/pages/PolicyDetailPage'), 'PolicyDetailPage') },
              { path: 'admin/usuarios', lazy: page(() => import('@/features/admin/AccountsPage'), 'AccountsPage') },
              { path: 'admin/contas', element: <Navigate to="/admin/usuarios" replace /> },
              { path: 'perfil', lazy: page(() => import('@/features/profile/ProfilePage'), 'ProfilePage') },
              { path: '*', element: <NotFound /> },
            ],
          },
        ],
      },
    ],
  },
];

export const createRouter = () => createBrowserRouter(routes);
